export type TextTrackFontFamily =
  | 'default'
  | 'monospaced_serif'
  | 'proportional_serif'
  | 'monospaced_sans_serif'
  | 'proportional_sans_serif'
  | 'casual'
  | 'cursive'
  | 'small_capitals'

export type TextTrackFontColor =
  | 'white'
  | 'black'
  | 'red'
  | 'green'
  | 'blue'
  | 'yellow'
  | 'magenta'
  | 'cyan'

export type TextTrackFontSize = '50' | '75' | '100' | '150' | '200'

export type TextTrackFontOpacity = '50' | '100'

export type TextTrackBackgroundColor =
  | 'white'
  | 'black'
  | 'red'
  | 'green'
  | 'blue'
  | 'yellow'
  | 'magenta'
  | 'cyan'

export type TextTrackBackgroundOpacity = '0' | '10' | '50' | '100'

export type TextTrackWindowColor =
  | 'white'
  | 'black'
  | 'red'
  | 'green'
  | 'blue'
  | 'yellow'
  | 'magenta'
  | 'cyan'

export type TextTrackWindowOpacity = '0' | '50' | '100'

export type TextTrackCharacterEdgeStyle =
  | 'none'
  | 'raised'
  | 'depressed'
  | 'uniform'
  | 'drop_shadow'

export interface TextTrackCustomSettings {
  fontFamily: TextTrackFontFamily
  fontColor: TextTrackFontColor
  fontSize: TextTrackFontSize
  fontOpacity: TextTrackFontOpacity
  backgroundColor: TextTrackBackgroundColor
  backgroundOpacity: TextTrackBackgroundOpacity
  windowColor: TextTrackWindowColor
  windowOpacity: TextTrackWindowOpacity
  characterEdgeStyle: TextTrackCharacterEdgeStyle
}

// Default values
export const DEFAULT_TEXT_TRACK_FONT_FAMILY: TextTrackFontFamily = 'default'
export const DEFAULT_TEXT_TRACK_FONT_COLOR: TextTrackFontColor = 'white'
export const DEFAULT_TEXT_TRACK_FONT_SIZE: TextTrackFontSize = '100'
export const DEFAULT_TEXT_TRACK_FONT_OPACITY: TextTrackFontOpacity = '100'
export const DEFAULT_TEXT_TRACK_BACKGROUND_COLOR: TextTrackBackgroundColor =
  'black'
export const DEFAULT_TEXT_TRACK_BACKGROUND_OPACITY: TextTrackBackgroundOpacity =
  '100'
export const DEFAULT_TEXT_TRACK_WINDOW_COLOR: TextTrackWindowColor = 'black'
export const DEFAULT_TEXT_TRACK_WINDOW_OPACITY: TextTrackWindowOpacity = '0'
export const DEFAULT_TEXT_TRACK_CHARACTER_EDGE_STYLE: TextTrackCharacterEdgeStyle =
  'none'

export const DEFAULT_TEXT_TRACK_CUSTOM_SETTINGS: TextTrackCustomSettings = {
  fontFamily: DEFAULT_TEXT_TRACK_FONT_FAMILY,
  fontColor: DEFAULT_TEXT_TRACK_FONT_COLOR,
  fontSize: DEFAULT_TEXT_TRACK_FONT_SIZE,
  fontOpacity: DEFAULT_TEXT_TRACK_FONT_OPACITY,
  backgroundColor: DEFAULT_TEXT_TRACK_BACKGROUND_COLOR,
  backgroundOpacity: DEFAULT_TEXT_TRACK_BACKGROUND_OPACITY,
  windowColor: DEFAULT_TEXT_TRACK_WINDOW_COLOR,
  windowOpacity: DEFAULT_TEXT_TRACK_WINDOW_OPACITY,
  characterEdgeStyle: DEFAULT_TEXT_TRACK_CHARACTER_EDGE_STYLE,
}

// Storage key
export const TEXT_TRACK_CUSTOM_SETTINGS_STORAGE_KEY =
  'pureya_text-track-custom-settings'

/**
 * Get text track custom settings from localStorage
 */
export const getTextTrackCustomSettings = (): TextTrackCustomSettings => {
  try {
    const textTrackCustomSettings = localStorage.getItem(
      TEXT_TRACK_CUSTOM_SETTINGS_STORAGE_KEY
    )

    return textTrackCustomSettings
      ? JSON.parse(textTrackCustomSettings)
      : DEFAULT_TEXT_TRACK_CUSTOM_SETTINGS
  } catch (ignore) {
    return DEFAULT_TEXT_TRACK_CUSTOM_SETTINGS
  }
}

/**
 * Save text track custom settings to localStorage
 */
export const saveTextTrackCustomSettings = (
  settings: TextTrackCustomSettings
): void => {
  try {
    localStorage.setItem(
      TEXT_TRACK_CUSTOM_SETTINGS_STORAGE_KEY,
      JSON.stringify(settings)
    )
  } catch (ignore) {
    // Ignore localStorage errors
  }
}

/**
 * Update text track custom settings
 */
export const updateTextTrackCustomSettings = (
  current: TextTrackCustomSettings,
  updates: Partial<TextTrackCustomSettings>
): TextTrackCustomSettings => {
  const updated = { ...current, ...updates }
  saveTextTrackCustomSettings(updated)
  return updated
}

/**
 * Reset text track custom settings to defaults
 */
export const resetTextTrackCustomSettings = (): TextTrackCustomSettings => {
  saveTextTrackCustomSettings(DEFAULT_TEXT_TRACK_CUSTOM_SETTINGS)
  return DEFAULT_TEXT_TRACK_CUSTOM_SETTINGS
}
